<script setup lang="ts">
import type { Component } from 'vue'
import { DEFAULT_TEXT_SETTINGS, DEFAULT_BACKGROUND_SETTINGS, DEFAULT_AUDIO_SETTINGS, DEFAULT_THEME_SETTINGS } from '~/stores/auth'
import { useGamesStore } from '~/stores/games'
import { stripHtml } from '~/utils/bio'
import { isProfileComplete } from '~~/shared/utils/profileSeo'
import DefaultLayout from '~/components/character/layout/DefaultLayout.vue'
import CompactLayout from '~/components/character/layout/CompactLayout.vue'
import SplitLayout from '~/components/character/layout/SplitLayout.vue'
import GridLayout from '~/components/character/layout/GridLayout.vue'

const LAYOUT_MAP: Record<string, Component> = {
  default: DefaultLayout,
  compact: CompactLayout,
  split: SplitLayout,
  grid: GridLayout
}

const props = defineProps<{
  username: string
}>()

const route = useRoute()
const { isCustomDomain, customDomain } = useCustomDomain()

// Fetch character data (public read via Supabase)
// await ensures data is available for defineOgImageComponent below
const { character, settings, audioPlaylist, links, achievements, clips, setupItems, friends, isLoading, error, refresh, asyncData, viewer } = useCharacterPage(props.username)
await asyncData

// Handle 404
if (error.value) {
  throw createError({
    statusCode: 404,
    statusMessage: 'Character not found',
    data: { username: props.username }
  })
}

// Blocked/removed profiles return HTTP 410 Gone to everyone except the owner
// and staff (who keep the appeal / moderation view). SSR-safe: crawlers and
// logged-out visitors get a real 410, never a soft-404. viewer flags are
// server-resolved (cookies forwarded via useRequestFetch in the composable).
if (character.value?.block && !viewer.value.is_owner && !viewer.value.is_staff) {
  throw createError({
    statusCode: 410,
    statusMessage: 'This page is no longer available'
  })
}

// Fetch games + track view + presence
const gamesStore = useGamesStore()
const supabase = useSupabaseClient()
let presenceChannel: ReturnType<typeof supabase.channel> | null = null

onMounted(async () => {
  // If the owner is viewing their own page, refresh to get latest settings
  if (character.value && authStore.character?.id === character.value.id) {
    await refresh()
  }

  if (character.value) {
    await gamesStore.fetchGames(character.value.id)
    // Track view (fire-and-forget). On custom domains a direct visit has no
    // referrer; report the domain so it shows up in the referrer breakdown.
    $fetch(`/api/character/${character.value.id}/view`, {
      method: 'POST',
      body: {
        referrer: (route.query.ref as string)
          || (route.query.utm_source as string)
          || document.referrer
          || (isCustomDomain.value ? window.location.hostname : null),
        sessionId: useStatisticsSession()
      }
    }).catch(() => {})

    // Join live visitor presence channel
    presenceChannel = supabase.channel(`visitors:${character.value.id}`)
    presenceChannel.subscribe(async (status) => {
      if (status === 'SUBSCRIBED' && presenceChannel) {
        await presenceChannel.track({ online_at: new Date().toISOString() })
      }
    })

    // Fetch friend status (fire-and-forget)
    fetchFriendStatus()
  }
})

onUnmounted(() => {
  if (presenceChannel) {
    supabase.removeChannel(presenceChannel)
    presenceChannel = null
  }
})

// Check ownership and admin status
const authStore = useAuthStore()
const overlay = useOverlay()
const isOwner = computed(() => {
  if (!authStore.userId || !character.value) return false
  return authStore.userId === character.value.id
})
// Any staff role (admin/manager/moderator) can moderate from character pages;
// the manage modal restricts what each role can actually do.
const isAdmin = computed(() => authStore.isStaff)
const isBlocked = computed(() => !!character.value?.block)

// Index hygiene for UGC profiles: blocked → noindex,nofollow (owner/staff-only
// view); thin/incomplete → noindex,follow (keeps link equity flowing without
// indexing an empty page); complete → index,follow.
const isProfileIndexable = computed(() => isProfileComplete({
  hasAvatar: !!settings.value?.avatar_url,
  bio: settings.value?.bio,
  linkCount: links.value.length
}))
useRobotsRule(computed(() => {
  if (isBlocked.value) return 'noindex, nofollow'
  return isProfileIndexable.value ? 'index, follow' : 'noindex, follow'
}))

async function openAdminModal() {
  if (!character.value) return
  const UserManagerModal = defineAsyncComponent(() => import('~/components/modals/admin/UserManagerModal.vue'))
  const modal = overlay.create(UserManagerModal)
  await modal.open({ userId: character.value.id }).result
}

async function openShareModal() {
  if (!character.value) return
  const ShareModal = defineAsyncComponent(() => import('~/components/modals/shared/ShareModal.vue'))
  overlay.create(ShareModal, {
    props: { username: character.value.username }
  }).open()
}

async function openReportModal() {
  if (!character.value) return
  const ReportModal = defineAsyncComponent(() => import('~/components/modals/ReportModal.vue'))
  const modal = overlay.create(ReportModal)
  await modal.open({ characterId: character.value.id, username: character.value.username }).result
}

// Friend status (for Add Friend button on other users' pages)
const friendStatus = ref<'none' | 'pending_sent' | 'pending_received' | 'accepted'>('none')
const friendshipId = ref<string | null>(null)
const friendActionLoading = ref(false)

async function fetchFriendStatus() {
  if (!authStore.userId || !character.value || isOwner.value) return
  try {
    const data = await $fetch<{ status: string, friendship_id: string | null }>(
      `/api/character/${authStore.userId}/friend/status`,
      { params: { target: character.value.id } }
    )
    friendStatus.value = data.status as any
    friendshipId.value = data.friendship_id
  } catch {
    // Not logged in or error
  }
}

async function handleFriendAction() {
  if (!authStore.userId || !character.value) return
  friendActionLoading.value = true
  try {
    if (friendStatus.value === 'none') {
      // Send friend request
      const data = await $fetch<{ friendship_id: string }>(`/api/character/${authStore.userId}/friend`, {
        method: 'POST',
        body: { username: character.value.username }
      })
      friendStatus.value = 'pending_sent'
      friendshipId.value = data.friendship_id
    } else if (friendStatus.value === 'pending_received') {
      // Accept friend request
      await $fetch(`/api/character/${authStore.userId}/friend/${friendshipId.value}`, { method: 'PUT' })
      friendStatus.value = 'accepted'
    }
  } catch {
    // Silently fail
  } finally {
    friendActionLoading.value = false
  }
}

const friendButtonLabel = computed(() => {
  switch (friendStatus.value) {
    case 'none': return 'Add Friend'
    case 'pending_sent': return 'Pending'
    case 'pending_received': return 'Accept'
    case 'accepted': return 'Friends'
    default: return 'Add Friend'
  }
})

const friendButtonIcon = computed(() => {
  switch (friendStatus.value) {
    case 'none': return 'i-lucide-user-plus'
    case 'pending_sent': return 'i-lucide-clock'
    case 'pending_received': return 'i-lucide-check'
    case 'accepted': return 'i-lucide-users'
    default: return 'i-lucide-user-plus'
  }
})

// Text settings from saved settings
const textSettings = computed(() => settings.value?.text ?? { ...DEFAULT_TEXT_SETTINGS })
const backgroundSettings = computed(() => settings.value?.background ?? { ...DEFAULT_BACKGROUND_SETTINGS })
const audioSettings = computed(() => settings.value?.audio ?? { ...DEFAULT_AUDIO_SETTINGS })
const themeSettings = computed(() => settings.value?.theme ?? { ...DEFAULT_THEME_SETTINGS })

const layoutComponent = computed(() => {
  const layout = settings.value?.layout ?? 'default'
  return LAYOUT_MAP[layout] || DefaultLayout
})

// Animated title effect (client-side only)
const animatedTitle = ref(character.value ? `@${character.value.username}` : '')
let titleTimeout: ReturnType<typeof setTimeout> | null = null

function startTitleAnimation() {
  if (!character.value) return
  const name = `@${character.value.username}`
  let index = 0
  let direction = 1 // 1 = typing, -1 = erasing

  function tick() {
    index += direction
    animatedTitle.value = name.slice(0, index)

    if (index >= name.length) {
      // Pause at full name, then start erasing
      direction = -1
      titleTimeout = setTimeout(tick, 2000)
    } else if (index <= 0) {
      // Pause at empty, then start typing
      direction = 1
      titleTimeout = setTimeout(tick, 500)
    } else {
      titleTimeout = setTimeout(tick, 250)
    }
  }

  // Start typing from scratch
  animatedTitle.value = ''
  titleTimeout = setTimeout(tick, 400)
}

function stopTitleAnimation() {
  if (titleTimeout) {
    clearTimeout(titleTimeout)
    titleTimeout = null
  }
}

// SSR title + template are set by useSeoMeta below (single source of truth);
// the client typing animation overwrites document.title after mount.

// Client: animate document.title directly (bypasses Unhead scheduling).
// On custom domains keep a constant "- mychar.gg" suffix so the platform
// stays visible in the tab.
const animatedTitleSuffix = isCustomDomain.value ? ' - mychar.gg' : ''
watch(animatedTitle, (val) => {
  if (import.meta.client) {
    const base = val || (character.value ? `@${character.value.username}` : 'Character')
    document.title = base + animatedTitleSuffix
  }
})

onMounted(() => {
  startTitleAnimation()
})

// Restart animation if character data loads/refreshes after mount
watch(character, (val) => {
  if (val && import.meta.client && !titleTimeout) {
    startTitleAnimation()
  }
})

onUnmounted(() => {
  stopTitleAnimation()
})

// What the page holds ("links, clips, and music"), so titles and descriptions
// describe this page instead of only repeating the username. Built only from
// server-rendered content: games load client-side, so they are not counted.
const contentSummary = computed(() => {
  const parts = [
    links.value.length ? 'links' : '',
    clips.value.length ? 'clips' : '',
    audioPlaylist.value.length ? 'music' : '',
    setupItems.value.length ? 'setup' : ''
  ].filter(Boolean).slice(0, 3)
  if (parts.length <= 1) return parts[0] ?? ''
  return `${parts.slice(0, -1).join(', ')}${parts.length > 2 ? ',' : ''} and ${parts.at(-1)}`
})

// Lengths in characters, not UTF-16 units: styled-letter bios (𝙃𝙚𝙡𝙡𝙤) use two
// units per letter and would otherwise count double
const charLength = (text: string) => [...text].length
const truncate = (text: string, max: number) => {
  const chars = [...text]
  return chars.length <= max ? text : `${chars.slice(0, max - 1).join('').replace(/\s+\S*$/, '').trimEnd()}…`
}

// Plain text bio for SEO (strip HTML tags from rich text), sized for the
// 120-160 char range crawlers expect. Short bios get a sentence about the page
// appended (the most complete version that fits in 160 chars); ASCII-art style
// characters (braille, box drawing) are dropped, and a bio with almost no
// letters counts as empty.
const plainBio = computed(() => {
  const u = character.value?.username
  const who = u ? `@${u}` : 'this'
  const lead = `See ${who}'s ${contentSummary.value || 'links'} on mychar.gg, the highly customizable link in bio.`
  const tail = ' Make your own page in a live drag-and-drop editor, free to start.'
  const text = stripHtml(settings.value?.bio || '')
    .replace(/[─-▟⠀-⣿]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  const hasBio = (text.match(/\p{L}/gu)?.length ?? 0) >= 3
  if (hasBio && charLength(text) >= 120) return truncate(text, 160)
  const candidates = hasBio
    ? [`${text} · ${lead}${tail}`, `${text} · ${lead} Make your own page, free to start.`, `${text} · ${lead}`, `${text} · More on mychar.gg, the highly customizable link in bio.`, `${text} · See more on mychar.gg.`]
    : [`${lead}${tail}`, lead]
  return candidates.find(c => charLength(c) <= 160) ?? truncate(hasBio ? text : lead, 160)
})

// Canonical URL: an active custom domain is the authoritative address for
// this character (on both hosts); otherwise mychar.gg/username.
const siteUrl = useRuntimeConfig().public.siteUrl
const canonicalUrl = computed(() => {
  const activeDomain = character.value?.customDomain
    || (isCustomDomain.value ? customDomain.value : null)
  if (activeDomain) return `https://${activeDomain}/`
  return `${siteUrl}/${props.username}`
})

useHead({
  link: [{ key: 'canonical', rel: 'canonical', href: () => canonicalUrl.value }]
})

// SEO meta (crawlers). Title pattern on the platform host:
// "{display} (@{username}) | Links and music on mychar.gg" (what the page holds,
// or "Link in bio"), falling back to "{name} | mychar.gg" when that would run
// past ~60 chars. A custom domain keeps "{name} - mychar.gg". The client typing
// animation overwrites document.title after mount; crawlers read this SSR title.
const seoTitleBase = computed(() => {
  const u = character.value?.username
  if (!u) return 'Character'
  const dn = settings.value?.display_name
  return dn ? `${dn} (@${u})` : `@${u}`
})
const seoTitle = computed(() => {
  const name = seoTitleBase.value
  if (isCustomDomain.value) return `${name} - mychar.gg`
  const summary = contentSummary.value
  const what = !summary || summary === 'links' ? 'Link in bio' : summary.charAt(0).toUpperCase() + summary.slice(1)
  const full = `${name} | ${what} on mychar.gg`
  return full.length <= 62 ? full : `${name} | mychar.gg`
})
useSeoMeta({
  title: () => seoTitle.value,
  // seoTitle already carries the brand
  titleTemplate: (t?: string | null) => t || 'mychar.gg',
  ogTitle: () => settings.value?.display_name || `@${character.value?.username}`,
  description: () => plainBio.value,
  ogDescription: () => plainBio.value,
  ogUrl: () => canonicalUrl.value,
  twitterCard: 'summary_large_image'
})

// JSON-LD: ProfilePage + Person. External social links become the Person's
// sameAs (entity reconciliation for search/AI). Skipped on blocked pages
// (which are noindexed); M3 will also gate on profile completeness.
const profileSameAs = computed(() =>
  links.value
    .filter(l => l.is_url && l.value)
    .map(l => (l.value.startsWith('http') ? l.value : `${l.link?.prefix || 'https://'}${l.value}`))
)
if (character.value && !isBlocked.value && isProfileIndexable.value) {
  // The Person MUST carry its own @id: without one, nuxt-schema-org merges it
  // into the sitewide Organization #identity node, producing an invalid mixed
  // node (a Person with a `logo`, brand sameAs polluting the user's identity).
  const personId = `${canonicalUrl.value}#person`
  useSchemaOrg([
    defineWebPage({ '@type': 'ProfilePage', 'mainEntity': { '@id': personId } }),
    definePerson({
      '@id': personId,
      'name': settings.value?.display_name || character.value.username,
      'alternateName': `@${character.value.username}`,
      'url': canonicalUrl.value,
      'image': settings.value?.avatar_url || undefined,
      'description': plainBio.value,
      'sameAs': profileSameAs.value.length ? profileSameAs.value : undefined
    })
  ])
}

// Dynamic OG image with character info
defineOgImageComponent('Character', {
  name: settings.value?.display_name || `@${character.value?.username}`,
  username: character.value?.username || '',
  description: stripHtml(settings.value?.bio || ''),
  avatarUrl: settings.value?.avatar_url || '',
  backgroundUrl: settings.value?.background_url || '',
  primaryColor: themeSettings.value.primaryColor,
  avatarShape: themeSettings.value.avatarShape,
  location: settings.value?.location || '',
  pronouns: settings.value?.pronouns || '',
  linkIcons: links.value.slice(0, 6).map(l => ({
    icon: l.link.icon,
    color: l.link.icon_color
  })),
  achievementIcons: achievements.value
    .filter(a => a.is_displayed)
    .slice(0, 5)
    .map(a => ({
      icon: a.achievement.icon,
      color: a.achievement.icon_color
    }))
})
</script>

<template>
  <div>
    <!-- Loading state -->
    <div
      v-if="isLoading"
      class="flex items-center justify-center min-h-dvh"
    >
      <UIcon
        name="i-lucide-loader-2"
        class="size-8 animate-spin text-primary-400"
      />
    </div>

    <!-- Blocked visitor view -->
    <div
      v-else-if="character && isBlocked && !isOwner && !isAdmin"
      class="flex items-center justify-center min-h-dvh"
    >
      <div class="text-center space-y-4">
        <div class="mx-auto flex items-center justify-center size-16 rounded-full bg-white/5">
          <UIcon
            name="i-lucide-clock"
            class="size-8 text-white/30"
          />
        </div>
        <h1 class="text-xl font-semibold text-white/60">
          This character page is currently unavailable
        </h1>
        <p class="text-sm text-white/30">
          This page has been temporarily restricted.
        </p>
      </div>
    </div>

    <!-- Blocked owner view -->
    <template v-else-if="character && isBlocked && isOwner">
      <CharacterBlockedOwnerView
        :character="character"
        @appeal-submitted="refresh()"
      />
    </template>

    <!-- Character page (read-only) -->
    <template v-else-if="character && settings">
      <CharacterPageShell
        :character="character"
        :settings="settings"
        :background-settings="backgroundSettings"
        :audio-settings="audioSettings"
        :theme-settings="themeSettings"
        :text-settings="textSettings"
        :audio-playlist="audioPlaylist"
        :total-views="character.totalViews"
        :is-owner="isOwner"
      >
        <component
          :is="layoutComponent"
          :character="character"
          :settings="settings"
          :text-settings="textSettings"
          :background-settings="backgroundSettings"
          :audio-settings="audioSettings"
          :theme-settings="themeSettings"
          :audio-playlist="audioPlaylist"
          :links="links"
          :achievements="achievements"
          :games="gamesStore.games"
          :clips="clips"
          :setup-items="setupItems"
          :friends="friends"
          :is-owner="isOwner"
        />

        <!-- Mobile FABs in bottom bar -->
        <template #mobile-fabs>
          <template v-if="isOwner || isAdmin || (!isOwner && authStore.userId)">
            <div class="w-px h-4 bg-white/10 shrink-0" />
            <button
              v-if="isAdmin"
              class="size-7 shrink-0 flex items-center justify-center rounded-full bg-red-500/30 text-red-300 active:bg-red-500/50 transition-colors cursor-pointer"
              @click="openAdminModal"
            >
              <UIcon
                name="i-lucide-shield-alert"
                class="size-3.5"
              />
            </button>
            <button
              v-if="!isOwner && authStore.userId && friendStatus !== 'accepted'"
              class="size-7 shrink-0 flex items-center justify-center rounded-full bg-white/10 text-white/40 active:text-white/70 transition-colors cursor-pointer"
              :disabled="friendActionLoading || friendStatus === 'pending_sent'"
              @click="handleFriendAction"
            >
              <UIcon
                v-if="friendActionLoading"
                name="i-lucide-loader-2"
                class="size-3.5 animate-spin"
              />
              <UIcon
                v-else
                :name="friendButtonIcon"
                class="size-3.5"
              />
            </button>
            <button
              v-if="!isOwner && authStore.userId"
              class="size-7 shrink-0 flex items-center justify-center rounded-full bg-white/10 text-white/40 active:text-white/70 transition-colors cursor-pointer"
              @click="openReportModal"
            >
              <UIcon
                name="i-lucide-flag"
                class="size-3.5"
              />
            </button>
            <NuxtLink
              v-if="isOwner"
              to="/customize"
              class="size-7 shrink-0 flex items-center justify-center rounded-full bg-primary-400/30 text-primary-300 active:bg-primary-400/50 transition-colors"
            >
              <UIcon
                name="i-lucide-paintbrush"
                class="size-3.5"
              />
            </NuxtLink>
            <button
              v-if="isOwner"
              class="size-7 shrink-0 flex items-center justify-center rounded-full bg-white/10 text-white/50 active:text-white/70 transition-colors cursor-pointer"
              @click="openShareModal"
            >
              <UIcon
                name="i-lucide-share-2"
                class="size-3.5"
              />
            </button>
          </template>
        </template>
      </CharacterPageShell>

      <!-- Action buttons (desktop only - right-center vertical stack, mobile uses bottom bar) -->
      <div
        v-if="isOwner || isAdmin || (!isOwner && authStore.userId)"
        class="hidden sm:flex fixed right-3 top-1/2 -translate-y-1/2 z-50 flex-col gap-2"
      >
        <!-- Admin -->
        <UTooltip
          v-if="isAdmin"
          text="Manage user"
          :content="{ side: 'left' }"
        >
          <button
            class="flex items-center justify-center size-9 rounded-xl bg-red-500/30 border border-red-400/40 text-red-200 hover:bg-red-500/40 hover:text-red-100 transition-all duration-200 backdrop-blur-md cursor-pointer"
            @click="openAdminModal"
          >
            <UIcon
              name="i-lucide-shield-alert"
              class="size-4"
            />
          </button>
        </UTooltip>

        <!-- Add Friend (non-owner, logged in, not already friends) -->
        <UTooltip
          v-if="!isOwner && authStore.userId && friendStatus !== 'accepted'"
          :text="friendButtonLabel"
          :content="{ side: 'left' }"
        >
          <button
            class="flex items-center justify-center size-9 rounded-xl bg-black/30 border border-white/12 text-white/80 hover:text-white hover:bg-black/40 transition-all duration-200 backdrop-blur-md cursor-pointer"
            :disabled="friendActionLoading || friendStatus === 'pending_sent'"
            @click="handleFriendAction"
          >
            <UIcon
              v-if="friendActionLoading"
              name="i-lucide-loader-2"
              class="size-4 animate-spin"
            />
            <UIcon
              v-else
              :name="friendButtonIcon"
              class="size-4"
            />
          </button>
        </UTooltip>

        <!-- Report (non-owner, logged in) -->
        <UTooltip
          v-if="!isOwner && authStore.userId"
          text="Report"
          :content="{ side: 'left' }"
        >
          <button
            class="flex items-center justify-center size-9 rounded-xl bg-black/30 border border-white/12 text-white/80 hover:text-white hover:bg-black/40 transition-all duration-200 backdrop-blur-md cursor-pointer"
            @click="openReportModal"
          >
            <UIcon
              name="i-lucide-flag"
              class="size-4"
            />
          </button>
        </UTooltip>

        <!-- Customize -->
        <UTooltip
          v-if="isOwner"
          text="Customize"
          :content="{ side: 'left' }"
        >
          <NuxtLink
            to="/customize"
            class="flex items-center justify-center size-9 rounded-xl bg-black/30 border border-white/12 text-white/80 hover:text-white hover:bg-black/40 transition-all duration-200 backdrop-blur-md"
          >
            <UIcon
              name="i-lucide-paintbrush"
              class="size-4"
            />
          </NuxtLink>
        </UTooltip>

        <!-- Share -->
        <UTooltip
          v-if="isOwner"
          text="Share"
          :content="{ side: 'left' }"
        >
          <button
            class="flex items-center justify-center size-9 rounded-xl bg-black/30 border border-white/12 text-white/80 hover:text-white hover:bg-black/40 transition-all duration-200 backdrop-blur-md cursor-pointer"
            @click="openShareModal"
          >
            <UIcon
              name="i-lucide-share-2"
              class="size-4"
            />
          </button>
        </UTooltip>
      </div>
    </template>
  </div>
</template>
